import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Trash2, Save, X, RefreshCw, Users, Shield, 
  Building2, Globe, Tag, GripVertical, RotateCcw, Eye, EyeOff,
  ChevronDown, ChevronUp, LayoutDashboard, BarChart3
} from 'lucide-react';

const ROLES = [
  { value: 'admin', label: 'Administrateur', color: 'bg-red-100 text-red-700' },
  { value: 'commercial', label: 'Commercial', color: 'bg-blue-100 text-blue-700' },
  { value: 'livreur', label: 'Livreur', color: 'bg-green-100 text-green-700' },
];

const COLOR_OPTIONS = [
  { label: 'Orange', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  { label: 'Rouge', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  { label: 'Jaune', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  { label: 'Vert', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  { label: 'Bleu', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  { label: 'Violet', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  { label: 'Rose', color: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
  { label: 'Gris', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
  { label: 'Indigo', color: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
  { label: 'Teal', color: 'bg-teal-100 text-teal-700', dot: 'bg-teal-500' },
];

// Collapsible Section Component
function Section({ title, icon: Icon, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] shadow-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-gray-500" />
          <h2 className="font-semibold text-gray-900">{title}</h2>
        </div>
        {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {isOpen && <div className="border-t border-gray-100">{children}</div>}
    </div>
  );
}

export default function Settings() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', nom: '', prenom: '', role: 'commercial' });

  // Settings state
  const [entreprise, setEntreprise] = useState({
    nom: '', adresse: '', code_postal: '', ville: '', telephone: '', email: '', siret: '', tva: '', logo_url: ''
  });
  const [ovh, setOvh] = useState({
    application_key: '', application_secret: '', consumer_key: '', price_limit: 5.0, default_email_server: 'ssl0.ovh.net'
  });
  const [statuts, setStatuts] = useState([]);
  const [showOvhSecrets, setShowOvhSecrets] = useState(false);
  const [savingEntreprise, setSavingEntreprise] = useState(false);
  const [savingOvh, setSavingOvh] = useState(false);
  const [savingStatuts, setSavingStatuts] = useState(false);
  const [newStatut, setNewStatut] = useState({ value: '', label: '', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' });

  // Dashboard KPI Cards
  const [kpiCards, setKpiCards] = useState([]);
  const [savingKpi, setSavingKpi] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    try {
      const [settingsRes, kpiRes] = await Promise.all([
        api.get('/settings'),
        api.get('/settings/dashboard-kpi').catch(() => ({ data: { cards: [] } }))
      ]);
      if (settingsRes.data.entreprise) setEntreprise(prev => ({ ...prev, ...settingsRes.data.entreprise }));
      if (settingsRes.data.ovh) setOvh(prev => ({ ...prev, ...settingsRes.data.ovh }));
      if (settingsRes.data.statuts) setStatuts(settingsRes.data.statuts);
      if (kpiRes.data.cards?.length) setKpiCards(kpiRes.data.cards);
    } catch (e) {
      console.error('Error loading settings:', e);
    }
  };

  useEffect(() => { 
    fetchUsers(); 
    fetchSettings();
  }, []);

  // Users CRUD
  const submit = async () => {
    try {
      if (editUser) {
        await api.put(`/users/${editUser.id}`, { nom: form.nom, prenom: form.prenom, role: form.role, ...(form.password ? { password: form.password } : {}) });
        toast.success('Utilisateur mis à jour');
      } else {
        await api.post('/users', form);
        toast.success('Utilisateur créé');
      }
      setShowForm(false);
      setEditUser(null);
      setForm({ email: '', password: '', nom: '', prenom: '', role: 'commercial' });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const startEdit = (u) => {
    setEditUser(u);
    setForm({ email: u.email, nom: u.nom, prenom: u.prenom, role: u.role, password: '' });
    setShowForm(true);
  };

  // Settings save handlers
  const saveEntreprise = async () => {
    setSavingEntreprise(true);
    try {
      await api.put('/settings/entreprise', entreprise);
      toast.success('Paramètres entreprise sauvegardés');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSavingEntreprise(false);
    }
  };

  const saveOvh = async () => {
    setSavingOvh(true);
    try {
      await api.put('/settings/ovh', ovh);
      toast.success('Paramètres OVH sauvegardés');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSavingOvh(false);
    }
  };

  const saveStatuts = async () => {
    setSavingStatuts(true);
    try {
      await api.put('/settings/statuts', { statuts });
      toast.success('Statuts sauvegardés');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSavingStatuts(false);
    }
  };

  const resetStatuts = async () => {
    if (!window.confirm('Réinitialiser tous les statuts aux valeurs par défaut ?')) return;
    try {
      const res = await api.post('/settings/statuts/reset');
      setStatuts(res.data.statuts);
      toast.success('Statuts réinitialisés');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const addStatut = () => {
    if (!newStatut.value || !newStatut.label) {
      toast.error('Identifiant et libellé requis');
      return;
    }
    if (statuts.some(s => s.value === newStatut.value)) {
      toast.error('Cet identifiant existe déjà');
      return;
    }
    setStatuts([...statuts, { ...newStatut, order: statuts.length + 1 }]);
    setNewStatut({ value: '', label: '', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' });
  };

  const updateStatut = (index, field, value) => {
    const updated = [...statuts];
    updated[index] = { ...updated[index], [field]: value };
    setStatuts(updated);
  };

  // KPI Card handlers
  const saveKpiCards = async () => {
    setSavingKpi(true);
    try {
      await api.put('/settings/dashboard-kpi', { cards: kpiCards });
      toast.success('Cartes KPI sauvegardées');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSavingKpi(false);
    }
  };

  const addKpiCard = () => {
    setKpiCards([...kpiCards, {
      id: `card_${Date.now()}`,
      nom: '',
      statuts: [],
      show_count: true,
      show_ca: true,
      order: kpiCards.length + 1
    }]);
  };

  const updateKpiCard = (index, field, value) => {
    const updated = [...kpiCards];
    updated[index] = { ...updated[index], [field]: value };
    setKpiCards(updated);
  };

  const toggleKpiStatut = (cardIndex, statutValue) => {
    const updated = [...kpiCards];
    const card = { ...updated[cardIndex] };
    const statuts_list = card.statuts || [];
    if (statuts_list.includes(statutValue)) {
      card.statuts = statuts_list.filter(s => s !== statutValue);
    } else {
      card.statuts = [...statuts_list, statutValue];
    }
    updated[cardIndex] = card;
    setKpiCards(updated);
  };

  const removeKpiCard = (index) => {
    setKpiCards(kpiCards.filter((_, i) => i !== index));
  };

  const deleteStatut = (index) => {
    if (statuts.length <= 1) {
      toast.error('Au moins un statut est requis');
      return;
    }
    setStatuts(statuts.filter((_, i) => i !== index));
  };

  const moveStatut = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= statuts.length) return;
    const updated = [...statuts];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setStatuts(updated);
  };

  const inputCls = "w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] bg-white";

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-[hsl(222,47%,11%)]">Paramètres</h1>
        <p className="text-gray-500 text-sm">Configuration du CRM et gestion de l'équipe</p>
      </div>

      {/* 1. Entreprise Settings */}
      <Section title="Paramètres de l'entreprise" icon={Building2} defaultOpen={false}>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Nom de l'entreprise</label>
              <input 
                className={inputCls} 
                value={entreprise.nom} 
                onChange={e => setEntreprise(p => ({...p, nom: e.target.value}))} 
                placeholder="IBN Services"
                data-testid="entreprise-nom"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Email</label>
              <input 
                type="email"
                className={inputCls} 
                value={entreprise.email} 
                onChange={e => setEntreprise(p => ({...p, email: e.target.value}))} 
                placeholder="contact@ibnservices.fr"
                data-testid="entreprise-email"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500 block mb-1">Adresse</label>
              <input 
                className={inputCls} 
                value={entreprise.adresse} 
                onChange={e => setEntreprise(p => ({...p, adresse: e.target.value}))} 
                placeholder="123 rue Example"
                data-testid="entreprise-adresse"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Code postal</label>
              <input 
                className={inputCls} 
                value={entreprise.code_postal} 
                onChange={e => setEntreprise(p => ({...p, code_postal: e.target.value}))} 
                placeholder="75001"
                data-testid="entreprise-cp"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Ville</label>
              <input 
                className={inputCls} 
                value={entreprise.ville} 
                onChange={e => setEntreprise(p => ({...p, ville: e.target.value}))} 
                placeholder="Paris"
                data-testid="entreprise-ville"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Téléphone</label>
              <input 
                className={inputCls} 
                value={entreprise.telephone} 
                onChange={e => setEntreprise(p => ({...p, telephone: e.target.value}))} 
                placeholder="01 23 45 67 89"
                data-testid="entreprise-tel"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">SIRET</label>
              <input 
                className={inputCls} 
                value={entreprise.siret} 
                onChange={e => setEntreprise(p => ({...p, siret: e.target.value}))} 
                placeholder="123 456 789 00012"
                data-testid="entreprise-siret"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">N° TVA</label>
              <input 
                className={inputCls} 
                value={entreprise.tva} 
                onChange={e => setEntreprise(p => ({...p, tva: e.target.value}))} 
                placeholder="FR12345678901"
                data-testid="entreprise-tva"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">URL du logo</label>
              <input 
                className={inputCls} 
                value={entreprise.logo_url} 
                onChange={e => setEntreprise(p => ({...p, logo_url: e.target.value}))} 
                placeholder="https://..."
                data-testid="entreprise-logo"
              />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button 
              onClick={saveEntreprise} 
              disabled={savingEntreprise}
              className="bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
              data-testid="save-entreprise-btn"
            >
              {savingEntreprise ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              Sauvegarder
            </button>
          </div>
        </div>
      </Section>

      {/* 2. OVH Settings */}
      <Section title="Paramètres OVH (Domaines & Emails)" icon={Globe} defaultOpen={false}>
        <div className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <strong>Attention :</strong> Ces clés permettent d'acheter des domaines. Ne les partagez pas.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Application Key</label>
              <input 
                type={showOvhSecrets ? "text" : "password"}
                className={inputCls} 
                value={ovh.application_key} 
                onChange={e => setOvh(p => ({...p, application_key: e.target.value}))} 
                placeholder="••••••••"
                data-testid="ovh-app-key"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Application Secret</label>
              <input 
                type={showOvhSecrets ? "text" : "password"}
                className={inputCls} 
                value={ovh.application_secret} 
                onChange={e => setOvh(p => ({...p, application_secret: e.target.value}))} 
                placeholder="••••••••"
                data-testid="ovh-app-secret"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Consumer Key</label>
              <input 
                type={showOvhSecrets ? "text" : "password"}
                className={inputCls} 
                value={ovh.consumer_key} 
                onChange={e => setOvh(p => ({...p, consumer_key: e.target.value}))} 
                placeholder="••••••••"
                data-testid="ovh-consumer-key"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Limite de prix (€)</label>
              <input 
                type="number"
                step="0.5"
                className={inputCls} 
                value={ovh.price_limit} 
                onChange={e => setOvh(p => ({...p, price_limit: parseFloat(e.target.value) || 0}))} 
                placeholder="5.00"
                data-testid="ovh-price-limit"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Serveur email par défaut</label>
              <input 
                className={inputCls} 
                value={ovh.default_email_server} 
                onChange={e => setOvh(p => ({...p, default_email_server: e.target.value}))} 
                placeholder="ssl0.ovh.net"
                data-testid="ovh-email-server"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setShowOvhSecrets(!showOvhSecrets)}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
              >
                {showOvhSecrets ? <EyeOff size={14} /> : <Eye size={14} />}
                {showOvhSecrets ? 'Masquer' : 'Afficher'} les clés
              </button>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button 
              onClick={saveOvh} 
              disabled={savingOvh}
              className="bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
              data-testid="save-ovh-btn"
            >
              {savingOvh ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              Sauvegarder
            </button>
          </div>
        </div>
      </Section>

      {/* Dashboard KPI Cards Configuration */}
      <Section title="Configuration des cartes Dashboard" icon={BarChart3} defaultOpen={false}>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-500 mb-3">
            Configurez les cartes de statistiques affichées sur le tableau de bord. Chaque carte regroupe des statuts et affiche le nombre et/ou le chiffre d'affaires.
          </p>

          {kpiCards.map((card, idx) => (
            <div key={card.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-testid={`kpi-card-${idx}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-gray-400 uppercase">Carte {idx + 1}</span>
                <button onClick={() => removeKpiCard(idx)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`kpi-card-delete-${idx}`}>
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Card name */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">Nom de la carte</label>
                <input
                  type="text"
                  value={card.nom}
                  onChange={e => updateKpiCard(idx, 'nom', e.target.value)}
                  placeholder="Ex: Incomplets, Prêt à livrer..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                  data-testid={`kpi-card-name-${idx}`}
                />
              </div>

              {/* Statuts checkboxes */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-500 mb-2">Statuts concernés</label>
                <div className="flex flex-wrap gap-2">
                  {statuts.map(s => {
                    const isChecked = (card.statuts || []).includes(s.value);
                    return (
                      <label key={s.value} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer border transition-all ${isChecked ? 'bg-[hsl(222,47%,11%)] text-white border-[hsl(222,47%,11%)]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleKpiStatut(idx, s.value)}
                          className="sr-only"
                        />
                        <span className={`w-2 h-2 rounded-full ${isChecked ? 'bg-white' : s.dot}`} />
                        {s.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Show toggles */}
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={card.show_count}
                    onChange={e => updateKpiCard(idx, 'show_count', e.target.checked)}
                    className="rounded border-gray-300 text-[hsl(222,47%,11%)] focus:ring-[hsl(222,47%,11%)]"
                  />
                  <span className="text-sm text-gray-700">Afficher le nombre</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={card.show_ca}
                    onChange={e => updateKpiCard(idx, 'show_ca', e.target.checked)}
                    className="rounded border-gray-300 text-[hsl(222,47%,11%)] focus:ring-[hsl(222,47%,11%)]"
                  />
                  <span className="text-sm text-gray-700">Afficher le CA</span>
                </label>
              </div>
            </div>
          ))}

          {/* Add card button */}
          <button
            onClick={addKpiCard}
            className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 text-gray-500 text-sm font-medium rounded-lg hover:border-gray-400 hover:text-gray-700 transition-colors w-full justify-center"
            data-testid="kpi-add-card"
          >
            <Plus size={14} /> Ajouter une carte
          </button>

          {/* Save button */}
          <div className="flex justify-end pt-2">
            <button
              onClick={saveKpiCards}
              disabled={savingKpi}
              className="flex items-center gap-2 px-4 py-2 bg-[hsl(222,47%,11%)] text-white text-sm font-medium rounded-lg hover:bg-[hsl(222,47%,15%)] transition-colors disabled:opacity-50"
              data-testid="kpi-save"
            >
              {savingKpi ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              Sauvegarder
            </button>
          </div>
        </div>
      </Section>

      {/* 3. Statuts Configuration */}
      <Section title="Gestion des statuts" icon={Tag} defaultOpen={false}>
        <div className="p-5 space-y-4">
          {/* Add new statut */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Ajouter un statut</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input 
                className={inputCls} 
                value={newStatut.value} 
                onChange={e => setNewStatut(p => ({...p, value: e.target.value.toLowerCase().replace(/\s+/g, '_')}))} 
                placeholder="identifiant_unique"
                data-testid="new-statut-value"
              />
              <input 
                className={inputCls} 
                value={newStatut.label} 
                onChange={e => setNewStatut(p => ({...p, label: e.target.value}))} 
                placeholder="Libellé affiché"
                data-testid="new-statut-label"
              />
              <select 
                className={inputCls}
                value={`${newStatut.color}|${newStatut.dot}`}
                onChange={e => {
                  const [color, dot] = e.target.value.split('|');
                  setNewStatut(p => ({...p, color, dot}));
                }}
                data-testid="new-statut-color"
              >
                {COLOR_OPTIONS.map(c => (
                  <option key={c.label} value={`${c.color}|${c.dot}`}>{c.label}</option>
                ))}
              </select>
              <button
                onClick={addStatut}
                className="bg-[hsl(222,47%,11%)] text-white px-4 py-2 rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] flex items-center justify-center gap-2"
                data-testid="add-statut-btn"
              >
                <Plus size={14} /> Ajouter
              </button>
            </div>
          </div>

          {/* Statuts list */}
          <div className="space-y-2">
            {statuts.map((s, idx) => (
              <div key={s.value} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-3" data-testid={`statut-row-${s.value}`}>
                <div className="flex flex-col gap-1">
                  <button onClick={() => moveStatut(idx, -1)} disabled={idx === 0} className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30">
                    <ChevronUp size={12} />
                  </button>
                  <button onClick={() => moveStatut(idx, 1)} disabled={idx === statuts.length - 1} className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30">
                    <ChevronDown size={12} />
                  </button>
                </div>
                <span className={`w-3 h-3 rounded-full ${s.dot}`} />
                <input 
                  className="flex-1 px-2 py-1 border border-gray-200 rounded text-sm"
                  value={s.label}
                  onChange={e => updateStatut(idx, 'label', e.target.value)}
                />
                <span className="text-xs text-gray-400 font-mono">{s.value}</span>
                <select 
                  className="px-2 py-1 border border-gray-200 rounded text-sm"
                  value={`${s.color}|${s.dot}`}
                  onChange={e => {
                    const [color, dot] = e.target.value.split('|');
                    updateStatut(idx, 'color', color);
                    updateStatut(idx, 'dot', dot);
                  }}
                >
                  {COLOR_OPTIONS.map(c => (
                    <option key={c.label} value={`${c.color}|${c.dot}`}>{c.label}</option>
                  ))}
                </select>
                <span className={`text-xs px-2 py-1 rounded-full ${s.color}`}>{s.label}</span>
                <button 
                  onClick={() => deleteStatut(idx)}
                  className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={14} className="text-red-400" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex justify-between pt-2">
            <button 
              onClick={resetStatuts}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            >
              <RotateCcw size={14} /> Réinitialiser
            </button>
            <button 
              onClick={saveStatuts} 
              disabled={savingStatuts}
              className="bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
              data-testid="save-statuts-btn"
            >
              {savingStatuts ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              Sauvegarder les statuts
            </button>
          </div>
        </div>
      </Section>

      {/* Users Management */}
      <Section title="Utilisateurs" icon={Users} defaultOpen={true}>
        <div>
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{users.length} utilisateur(s)</span>
            <button
              onClick={() => { setShowForm(true); setEditUser(null); setForm({ email: '', password: '', nom: '', prenom: '', role: 'commercial' }); }}
              className="flex items-center gap-2 bg-[hsl(222,47%,11%)] text-white px-4 py-2 rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] transition-colors"
              data-testid="add-user-btn"
            >
              <Plus size={14} />
              Ajouter
            </button>
          </div>

          {/* Form */}
          {showForm && (
            <div className="p-5 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 mb-4">{editUser ? 'Modifier' : 'Nouvel'} utilisateur</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Prénom</label>
                  <input className={inputCls} value={form.prenom} onChange={e => setForm(p=>({...p,prenom:e.target.value}))} placeholder="Jean" data-testid="user-prenom-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Nom</label>
                  <input className={inputCls} value={form.nom} onChange={e => setForm(p=>({...p,nom:e.target.value}))} placeholder="Dupont" data-testid="user-nom-input" />
                </div>
                {!editUser && (
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Email</label>
                    <input type="email" className={inputCls} value={form.email} onChange={e => setForm(p=>({...p,email:e.target.value}))} placeholder="jean@ibnservices.fr" data-testid="user-email-input" />
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{editUser ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe'}</label>
                  <input type="password" className={inputCls} value={form.password} onChange={e => setForm(p=>({...p,password:e.target.value}))} placeholder="••••••••" data-testid="user-password-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Rôle</label>
                  <select className={inputCls} value={form.role} onChange={e => setForm(p=>({...p,role:e.target.value}))} data-testid="user-role-select">
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={submit} className="bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm hover:bg-emerald-700 flex items-center gap-2" data-testid="save-user-btn">
                  <Save size={14} />{editUser ? 'Mettre à jour' : 'Créer'}
                </button>
                <button onClick={() => { setShowForm(false); setEditUser(null); }} className="border border-gray-200 px-4 py-2 rounded-lg text-sm hover:bg-white flex items-center gap-2">
                  <X size={14} />Annuler
                </button>
              </div>
            </div>
          )}

          {/* List */}
          {loading ? (
            <div className="p-8 flex justify-center"><RefreshCw size={20} className="animate-spin text-gray-400" /></div>
          ) : (
            <div className="divide-y divide-gray-50">
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[hsl(222,47%,11%)] rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">{u.prenom?.[0]}{u.nom?.[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{u.prenom} {u.nom}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${ROLES.find(r=>r.value===u.role)?.color || 'bg-gray-100 text-gray-600'}`}>
                      {ROLES.find(r=>r.value===u.role)?.label || u.role}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.actif ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                      {u.actif ? 'Actif' : 'Inactif'}
                    </span>
                    <button onClick={() => startEdit(u)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid={`edit-user-${u.id}`}>
                      <Edit2 size={14} className="text-gray-500" />
                    </button>
                    <button onClick={() => deleteUser(u.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" data-testid={`delete-user-${u.id}`}>
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
